import { useMutation, useQueryClient } from '@tanstack/react-query';
import { candidateAppService } from '../services/candidate-app.service';
import { useCandidateSession } from '../context/CandidateSessionContext';
import type { UpdateCandidateProfileRequest, CandidateProfile } from '../types/candidate-app.types';

interface UseUpdateCandidateProfileResult {
  updateProfile: (request: UpdateCandidateProfileRequest) => Promise<CandidateProfile>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function useUpdateCandidateProfile(): UseUpdateCandidateProfileResult {
  const { isAnonymous, shareId } = useCandidateSession();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (request: UpdateCandidateProfileRequest) =>
      isAnonymous
        ? candidateAppService.updateProfileByShareId(shareId!, request)
        : candidateAppService.updateProfile(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: isAnonymous ? ['candidate-profile', 'anonymous', shareId] : ['candidate-profile'],
      });
    },
  });

  return {
    updateProfile: (request: UpdateCandidateProfileRequest) => mutation.mutateAsync(request),
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}
